<template>
  <div class="line-link" v-show="!isLoading">
    <the-heading class="pt-1 pb-5 lg:pt-4 lg:pb-8">{{ $t('lineLink.heading') }}</the-heading>
    <the-section class="lg:mb-4 mb-1">
      <p v-if="isFromLogin" class="mb-8 text-red-150">{{ $t('lineLink.content.notLinkLineByLogin') }}</p>
      <p class="mb-5">{{ $t('lineLink.content.instruction') }}</p>
      <el-form
        ref="form"
        :model="form"
        label-position="top"
        class="ec-line039__form-contact"
        :rules="rules"
        @submit.native.prevent="onSubmit"
      >
        <el-form-item :label="$t('common.memberInformation.email')" prop="email">
          <el-input
            type="text"
            v-model="form.email"
            :placeholder="$t('common.informationPlaceholder.emailPlaceholder')"
            :maxlength="255"
          ></el-input>
          <template #error="{ error }">
            <p v-if="error" class="whitespace-pre-wrap el-form-item__error">
              {{ error }}
            </p>
          </template>
        </el-form-item>
        <el-form-item>
          <line-button @click="onSubmit">{{ $t('common.action.lineLink') }}</line-button>
        </el-form-item>
      </el-form>
    </the-section>
    <line-notify-dialog :visible.sync="visible" />
  </div>
</template>

<script>
import liff from '@line/liff'
import TheSection from '@/components/TheSection.vue'
import TheHeading from '@/components/TheHeading.vue'
import LineButton from '@/components/LineButton.vue'
import LineNotifyDialog from '@/components/line-link/LineNotifyDialog.vue'
import { AuthService, LineLinkService } from '@/services'
import mixinAuth from '@/mixins/auth'
import BreadcrumbMixin from '@/mixins/breadcrumb'
import { LINE_LOGIN_FROM_LOGIN_PAGE } from '@/constants'

export default {
  name: 'EC_LINE039',
  mixins: [mixinAuth, BreadcrumbMixin],
  components: {
    TheSection,
    TheHeading,
    LineButton,
    LineNotifyDialog,
  },

  data() {
    const validateEmailNotRegistered = (rule, value, callback) => {
      if (this.isValidEmailNotYetRegistered) {
        callback(new Error(this.$t('common.validate.email-not-yet-registered')))
      } else {
        callback()
      }
    }
    return {
      form: {
        email: '',
      },

      visible: false,

      isValidEmailNotYetRegistered: false,

      rules: {
        email: [
          this.Utils.validate.required(this.$t('common.validate.field.email')),
          this.Utils.validate.emailFormat(this.$t('common.validate.field.email')),
          { validator: validateEmailNotRegistered, trigger: ['submit'] },
        ],
      },

      isFromLogin: false,
      isLoading: false,
    }
  },
  created() {
    this.isFromLogin = Boolean(this.$route.query[LINE_LOGIN_FROM_LOGIN_PAGE])

    this.isLoading = true

    AuthService.login({
      accessToken: liff.getAccessToken(),
    })
      .then(({ data }) => {
        if (data.customer) {
          this.handleError({ message: this.$t('common.messages.lineHasDifferentCustomer') })
          liff.logout()
          this.handleLogout(this.$routePage.EC_LINE001)
        }
      })
      .finally(() => (this.isLoading = false))
  },
  methods: {
    onSubmit() {
      this.isValidEmailNotYetRegistered = false
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          this.handleError({ message: this.$t('common.messages.hasInvalidData') })
          return
        }

        const loading = this.$loading({
          lock: true,
        })
        try {
          const profile = await liff.getProfile()

          const { data } = await LineLinkService.lineLink({
            email: this.form.email,
            lineId: profile.userId,
          })

          if (data.status === this.Constants.LINE_LINK_STATUS.EMAIL_NOT_YET_REGISTERED) {
            this.isValidEmailNotYetRegistered = true
            this.$refs.form.validate((valid) => {
              if (!valid) {
                this.handleError({ message: this.$t('common.messages.hasInvalidData') })
                return
              }
            })
            return
          }

          this.form.email = ''

          this.$alert({
            message: this.$t('common.messages.sendMailLineLinkSuccess'),
            type: 'success',
          })
        } catch (error) {
          this.handleError(error)
        } finally {
          loading.close()
        }
      })
    },
  },
  metaInfo() {
    return {
      title: this.$t('lineLink.metaTitle'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('lineLink.description', { shop_name: this.shopName }),
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.$t('lineLink.keyword', { shop_name: this.shopName }),
        },
      ],
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/css/pages/ec-line039.scss';
</style>
